// src/pages/Ads/Reserve/AdsReserveCreate.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { HeaderIconL } from "../../../../assets/components/HeaderGroup"
import left from "../../../../assets/icons/Arrow/left.svg"
import { SingleButton, SplitRowHalf } from "../../../../assets/components/ButtonGroup";
import Reserve1 from "./Reserve1"
import Reserve2 from "./Reserve2"
import Reserve3 from "./Reserve3"
import { useFixedLayout } from "../../../../hooks/useFixedLayout";
import { STRAIGHT_OPTIONS } from "./straightOptions";

// ──────────────────────────────────────────────
// 공통 유틸
// ──────────────────────────────────────────────
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const koreanToEnglishDayMap = {
  월: "Mon",
  화: "Tue",
  수: "Wed",
  목: "Thu",
  금: "Fri",
  토: "Sat",
  일: "Sun",
};


// ──────────────────────────────────────────────
// 메인 컨테이너
// ──────────────────────────────────────────────
const AdsReserveCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state?.initialData ?? null; // 나중에 수정용으로도 재사용 가능
  const mode = initialData ? "edit" : "create";

  const { headerRef, footerRef, headerSpace, bottomSpace } = useFixedLayout();

  const [step, setStep] = useState(1);
  const [weeklyRangeValid, setWeeklyRangeValid] = useState(true);
  const [monthlyRangeValid, setMonthlyRangeValid] = useState(true);

  // step1
  const [title, setTitle] = useState(initialData?.title ?? "");

  // step2
  const [isTimeValid, setIsTimeValid] = useState(false);
  const [repeatCycle, setRepeatCycle] = useState(
    initialData?.repeat_type ?? ""
  );

  const [repeatCount, setRepeatCount] = useState(() => {
    if (initialData) {
      return initialData.repeat_count ?? 0;
    }
    return 0;
  });

  const getEffectiveRepeatCount = () => {
    if (repeatCycle === "daily") return repeatCount;
    if (repeatCycle === "weekly") return selectedWeekdays.length;
    if (repeatCycle === "monthly") return selectedDates.length;
    return 0;
  };

  const [start, setStart] = useState(
    initialData?.start_date ? new Date(initialData.start_date) : null
  );
  const [end, setEnd] = useState(
    initialData?.end_date ? new Date(initialData.end_date) : null
  );

  const [selectedWeekdays, setSelectedWeekdays] = useState(() => {
    if (!initialData?.weekly_days) return [];
    const engToKo = {
      Mon: "월",
      Tue: "화",
      Wed: "수",
      Thu: "목",
      Fri: "금",
      Sat: "토",
      Sun: "일",
    };
    return initialData.weekly_days.map((eng) => engToKo[eng] || eng);
  });

  const [selectedDates, setSelectedDates] = useState(
    initialData?.monthly_days ?? []
  ); // YYYY-MM-DD[]
  const [timeSelections, setTimeSelections] = useState(
    initialData?.repeat_type === "daily"
      ? initialData.upload_times ?? []
      : []
  );
  const [repeatTime, setRepeatTime] = useState(
    initialData?.repeat_type === "daily"
      ? ""
      : initialData?.upload_times?.[0] ?? ""
  );

  // step3
  const [straightKey, setStraightKey] = useState(() => {
    if (!initialData?.straight) return null;

    // 1) 이미 key("gallery" 등)로 저장된 경우
    const byKey = STRAIGHT_OPTIONS.find(
      (o) => o.key === initialData.straight
    );
    if (byKey) return byKey.key;

    // 2) 예전처럼 문구("내 사진으로 이미지 생성")로 저장된 경우
    const byLabel = STRAIGHT_OPTIONS.find(
      (o) => o.straight === initialData.straight
    );
    return byLabel?.key ?? null;
  });

  // 월/요일/날짜 선택용 바텀시트 상태 (기존 로직 재활용)
  const [openRepeatOptionSheet, setOpenRepeatOptionSheet] = useState(false);

  // monthly DayPicker용
  const [openMonthlyCalendar, setOpenMonthlyCalendar] = useState(false);

  // daily에서 repeatCount 변경 시 timeSelections 길이 보정
  useEffect(() => {
    if (repeatCycle !== "daily") return;
    setTimeSelections((prev) => {
      const next = prev.slice(0, repeatCount);
      while (next.length < repeatCount) next.push("");
      return next;
    });
  }, [repeatCount, repeatCycle]);



  // ─────────────── validation & payload 생성 ───────────────
  const hasStep1 = () => title.trim().length > 0 && title.trim().length <= 20;

  const hasStep2 = () => {
    if (!start || !end) return false;
    if (!repeatCycle) return false;

    // daily: 기존 로직 그대로 유지
    if (repeatCycle === "daily") {
      const rc = Number(repeatCount);
      if (!rc || rc < 1) return false;

      const arr = timeSelections.slice(0, rc);
      if (arr.length < rc) return false;
      if (arr.some((t) => !t)) return false;

      if (!isTimeValid) return false; // 시간 유효성(현재 시각 이전 선택 여부)까지 반영

      return true;
    }

    // weekly: 요일 1개 이상 + 시간 1개만 입력하면 OK
    if (repeatCycle === "weekly") {
      if (selectedWeekdays.length < 1) return false;
      if (!repeatTime) return false;
      if (!weeklyRangeValid) return false;
      return true;
    }

    // monthly: 날짜 1개 이상 + 시간 1개만 입력하면 OK
    if (repeatCycle === "monthly") {
      if (selectedDates.length < 1) return false;
      if (!repeatTime) return false;
      if (!monthlyRangeValid) return false;
      if (!isTimeValid) return false;
      return true;
    }

    return false;
  };

  const hasStep3 = () => !!straightKey;

  const buildUploadTimes = () => {
    if (repeatCycle === "daily") {
      return timeSelections.slice(0, repeatCount).filter(Boolean);
    }
    return [repeatTime];
  };

  const buildWeeklyDays = () => {
    if (repeatCycle !== "weekly") return null;
    return selectedWeekdays.map((day) => koreanToEnglishDayMap[day]);
  };

  const buildMonthlyDays = () => {
    if (repeatCycle !== "monthly") return null;
    return selectedDates;
  };

  // const selectedStraightObj = STRAIGHT_OPTIONS.find(
  //   (o) => o.key === straightKey
  // );
  // const straightValue = selectedStraightObj?.straight ?? null;

  const handleSubmit = async () => {
    if (!hasStep1() || !hasStep2() || !hasStep3()) {
      alert("모든 예약 정보를 입력해 주세요.");
      return;
    }

    const payloadBase = {
      user_id: localStorage.getItem("user_id"),
      title: title.trim(),
      start_date: formatDateLocal(start),
      end_date: formatDateLocal(end),
      repeat_type: repeatCycle,
      repeat_count: getEffectiveRepeatCount(),
      upload_times: buildUploadTimes(),
      weekly_days: buildWeeklyDays(),
      monthly_days: buildMonthlyDays(),
      straight: straightKey,
    };

    // start_date 기준으로 이동할 탭 결정
    //   - 오늘 또는 과거 → running(예약 실행 중)
    //   - 내일 이후     → waiting(예약 대기 중)
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const startDateOnly = start
      ? new Date(start.getFullYear(), start.getMonth(), start.getDate())
      : startOfToday;

    const initialTab =
      startDateOnly.getTime() > startOfToday.getTime() ? "waiting" : "running";

    try {
      if (mode === "edit") {
        const payload = {
          ...payloadBase,
          reserve_id: initialData.reserve_id,
        };
        await axios.post(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/update`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        alert("예약이 수정되었습니다");
        // 필요하면 location.state 등으로 리스트 리프레시 처리
      } else {
        await axios.post(
          `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/insert`,
          payloadBase,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      navigate("/ads/main", {
        state: {
          tab: "reserve",
          toast: "예약되었습니다",
          initialTab
        }
      });
    } catch (err) {
      console.error("예약 저장/수정 실패:", err);
      alert("예약 저장에 실패했습니다.");
    }
  };

  const handleBack = () => {
    if (step === 1) navigate("/ads/main", { state: { tab: "reserve" } });
    else setStep((s) => s - 1);
  };

  // ──────────────────────────────────────────────
  // 렌더
  // ──────────────────────────────────────────────
  return (
    <div className="h-dvh flex flex-col">
      {/* 상단 헤더 */}
      <div
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-10"
      >
        <HeaderIconL
          icon={left}
          onIconClick={handleBack}
          title="예약 알림 설정"
        />
      </div>

      {/* 내용 */}
      <div
        className="flex-1 overflow-auto"
        style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}
      >
        {step === 1 && (
          <Reserve1 title={title} setTitle={setTitle} hasStep1={hasStep1} />
        )}

        {step === 2 && (
          <Reserve2
            repeatCycle={repeatCycle}
            setRepeatCycle={setRepeatCycle}
            repeatCount={repeatCount}
            setRepeatCount={setRepeatCount}
            start={start}
            end={end}
            setStart={setStart}
            setEnd={setEnd}
            selectedWeekdays={selectedWeekdays}
            setSelectedWeekdays={setSelectedWeekdays}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            timeSelections={timeSelections}
            setTimeSelections={setTimeSelections}
            repeatTime={repeatTime}
            setRepeatTime={setRepeatTime}
            openRepeatOptionSheet={openRepeatOptionSheet}
            setOpenRepeatOptionSheet={setOpenRepeatOptionSheet}
            openMonthlyCalendar={openMonthlyCalendar}
            setOpenMonthlyCalendar={setOpenMonthlyCalendar}
            onWeeklyRangeValidChange={setWeeklyRangeValid}
            onMonthlyRangeValidChange={setMonthlyRangeValid}
            onTimeValidChange={setIsTimeValid}
          />
        )}

        {step === 3 && (
          <Reserve3
            straightKey={straightKey}
            setStraightKey={setStraightKey}
          />
        )}
      </div>

      {/* 하단 버튼 영역 */}
      <div
        ref={footerRef}
        className="fixed left-0 right-0 bottom-0"
      >
        {step === 1 && (
          <SingleButton
            disabled={!hasStep1()}
            onClick={() => hasStep1() && setStep(2)}
          >
            다음
          </SingleButton>
        )}

        {step === 2 && (
          <SingleButton
            disabled={!hasStep2()}
            onClick={() => hasStep2() && setStep(3)}
          >
            다음
          </SingleButton>
        )}

        {step === 3 && (
          <div>
            <SplitRowHalf
              leftText={"이전"}
              leftType={"border"}
              onLeft={() => setStep(2)}
              rightText={"예약하기"}
              onRight={() => handleSubmit()}
              rightDisabled={!hasStep3() || !hasStep2() || !hasStep1()}

            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsReserveCreate;